import { useActiveBarPosition } from '@odc-shared/hooks/useActiveBarPosition';
import { ArrowLeft, ArrowRight } from '@tencent/odc-iconpack';
import { Tooltip } from '@tencent/one-design-next';
import classNames from 'classnames';
import React, { Ref, forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import './index.scss';

export type TabsDataItem = {
  label: string | React.ReactNode;
  value: string | number;
  disabled?: boolean;
  disabledTooltip?: string;
  children?: React.ReactNode;
};
export interface ITabs {
  className?: string;
  style?: React.CSSProperties;
  data: TabsDataItem[];
  onChange?: (value: string | number, data: TabsDataItem) => void;
  defaultActiveValue?: string | number;
  value?: string | number;
  showPanel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  type?: 'line' | 'card' | 'bookmark' | 'navigation';
  align?: 'left' | 'center' | 'right';
  tabBarClassName?: string;
  tabBarStyle?: React.CSSProperties;
  tabContentClassName?: string;
  tabContentStyle?: React.CSSProperties;
  onTabClick?: (value: string | number, data: TabsDataItem) => void;
}

const Tabs = forwardRef((props: ITabs, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    style,
    data = [],
    onChange,
    defaultActiveValue,
    size = 'md',
    type = 'line',
    align = 'left',
    tabBarClassName,
    tabBarStyle,
    tabContentClassName,
    tabContentStyle,
    onTabClick,
    showPanel = true,
    value,
  } = props;
  const [currentActiveValueInline, setCurrentActiveValue] = useState<string | number>(
    value || defaultActiveValue || (data.length > 0 ? data[0].value : ''),
  );
  const currentActiveValue = useMemo(() => value ?? currentActiveValueInline, [value, currentActiveValueInline]);

  const [scrollLeft, setScrollLeft] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  // 存储每个tab label的ref和是否溢出
  const labelRefs = useRef<{ [key: string | number]: HTMLDivElement | null }>({});
  const [overflowTabs, setOverflowTabs] = useState<Set<string | number>>(new Set());

  const { tabRefs, activeBarStyle } = useActiveBarPosition({
    currentActiveValue,
    data,
  });

  /**
   * 检查tab label是否溢出
   */
  const checkLabelOverflow = () => {
    const newOverflowTabs = new Set<string | number>();

    data.forEach((item) => {
      const labelElement = labelRefs.current[item.value];
      if (labelElement) {
        // 检查scrollWidth是否大于144px
        if (labelElement.scrollWidth > 144) {
          newOverflowTabs.add(item.value);
        }
      }
    });

    setOverflowTabs(newOverflowTabs);
  };
  /**
   * 监听数据变化和尺寸变化
   */
  useEffect(() => {
    const timer = setTimeout(checkLabelOverflow, 0);
    return () => clearTimeout(timer);
  }, [data]);
  /**
   * 检查是否需要显示箭头
   * @returns
   */
  const checkScrollButtons = () => {
    if (!tabBarRef.current || !tabListRef.current) return;

    const container = tabBarRef.current;
    const list = tabListRef.current;

    const containerWidth = container.clientWidth;
    const listWidth = list.scrollWidth;
    const currentScrollLeft = list.scrollLeft;

    const rightEdgeBtnWidth = 40;

    setShowLeftArrow(currentScrollLeft > 0);
    setShowRightArrow(currentScrollLeft < listWidth - containerWidth - rightEdgeBtnWidth);
  };

  /**
   * 监听尺寸变化和滚动
   */
  useEffect(() => {
    checkScrollButtons();

    const resizeObserver = new ResizeObserver(checkScrollButtons);
    if (tabBarRef.current) {
      resizeObserver.observe(tabBarRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [data]);

  /**
   * 滚动处理
   * @param direction
   * @returns
   */
  const handleScroll = (direction: 'left' | 'right') => {
    if (!tabListRef.current) return;

    const scrollAmount = 200; // 每次滚动的距离
    const currentScroll = tabListRef.current.scrollLeft;
    const newScrollLeft = direction === 'left'
      ? Math.max(0, currentScroll - scrollAmount)
      : currentScroll + scrollAmount;

    tabListRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });
  };

  /**
   * 滚动到激活的tab
   * @returns
   */
  const scrollToActiveTab = () => {
    if (!tabListRef.current || !tabRefs.current[currentActiveValue]) return;

    const container = tabListRef.current;
    const activeTab = tabRefs.current[currentActiveValue];

    if (activeTab) {
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      if (tabRect.right > containerRect.right) {
        // tab在右侧溢出
        const scrollAmount = tabRect.right - containerRect.right + 20;
        container.scrollLeft += scrollAmount;
      } else if (tabRect.left < containerRect.left) {
        // tab在左侧溢出
        const scrollAmount = containerRect.left - tabRect.left + 20;
        container.scrollLeft -= scrollAmount;
      }
    }
  };

  /**
   * 当激活tab改变时，滚动到可视区域
   */
  useEffect(() => {
    scrollToActiveTab();
  }, [currentActiveValue]);

  /**
   * tab 切换处理
   * @param value
   * @param item
   */
  const handleTabChange = (value: string | number, item: TabsDataItem) => {
    setCurrentActiveValue(value);
    if (onChange) {
      onChange?.(value, item);
    }
  };

  return (
    <div
      className={classNames('odc-tabs', className, {
        [`odc-tabs-${size}`]: size,
        [`odc-tabs-${type}`]: type,
        [`odc-tabs-align-${align}`]: align,
      })}
      style={style}
      ref={ref}
    >
      <div
        className={classNames('odc-tabs-bar', tabBarClassName, {
          'odc-tabs-bar-scrollable': showLeftArrow || showRightArrow,
        })}
        style={tabBarStyle}
        ref={tabBarRef}
      >
        {showLeftArrow && type === 'bookmark' && (
          <div
            className="odc-tabs-nav-button odc-tabs-nav-left"
            onClick={() => handleScroll('left')}
          >
            <ArrowLeft />
          </div>
        )}

        <div
          className="odc-tabs-list"
          ref={tabListRef}
          onScroll={checkScrollButtons}
        >
          {data.map((item, index) => (
            <Tooltip
              key={`odc-tabs_${item.value}_popup`}
              popup={item.disabled ? item.disabledTooltip : ''}
              arrowed={false}
              visible={item.disabled && !!item.disabledTooltip ? undefined : false}
              placement="bottom"
              popupStyle={{
                maxWidth: '500px',
              }}
            >
              <div
                key={`odc-tabs_${item.value}`}
                ref={(el) => {
                  tabRefs.current[item.value] = el;
                }}
                className={classNames('odc-tabs-tab', {
                  'odc-tabs-tab-active': item.value === currentActiveValue,
                  'odc-tabs-tab-disabled': item.disabled,
                  'odc-tabs-tab-active-last': data[index + 1]?.value === currentActiveValue,
                  'odc-tabs-tab-active-next': data[index - 1]?.value === currentActiveValue,
                })}
                onClick={() => {
                  if (!item.disabled) {
                    onTabClick?.(item.value, item);
                    handleTabChange(item.value, item);
                  }
                }}
              >
                <Tooltip
                  popup={item.label}
                  arrowed={false}
                  placement="bottom"
                  popupStyle={{
                    maxWidth: '500px',
                    backgroundColor: '#fff',
                    color: 'var(--odc-main-color)',
                  }}
                  visible={overflowTabs.has(item.value) ? undefined : false}
                  offset={{
                    mainAxis: 12,
                  }}
                >
                  <div className="odc-tabs-tab-label" ref={(el) => { labelRefs.current[item.value] = el; }}>{item.label}</div>
                </Tooltip>
                <div className="odc-tabs-tab-indicator"></div>
              </div>
            </Tooltip>
          ))}
          {type !== 'bookmark' && type !== 'navigation' && (
            <div
              className={classNames('odc-tabs-active-bar', {
                [`odc-tabs-active-bar-${type}`]: type,
              })}
              style={{
                width: `${activeBarStyle.width}px`,
                transform: activeBarStyle.transform,
              }}
            ></div>)}
        </div>

        {showRightArrow && type === 'bookmark' && (
          <div
            className="odc-tabs-nav-button odc-tabs-nav-right"
            onClick={() => handleScroll('right')}
          >
            <ArrowRight />
          </div>
        )}
      </div>

      {showPanel && (
        <div className={classNames('odc-tabs-content', tabContentClassName)} style={tabContentStyle}>
          {data.map((item) => (
            <div
              key={item.value}
              className={classNames('odc-tabs-pane', {
                'odc-tabs-pane-active': item.value === currentActiveValue,
              })}
            >
              {item.value === currentActiveValue && item.children}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

Tabs.displayName = 'Tabs';
export default Tabs;